/**
 * A creator's own capsules and FlashBooks, as a profile's two work tabs show them.
 *
 * A profile's **Capsules** and **Flashbooks** tabs are the classroom's grids one
 * screen over (see `#/components/work`): the same cards, the same two-up grid, the
 * same first-cell way in. What differs is whose work it is — a room's, or the
 * account whose page this is — and which of the account's records are fit to show.
 *
 * This is the rule that decides that, and it is *pure* on purpose: the three
 * exclusions below are the whole content of the feature's privacy story, and they
 * can be tested without a device, a network or a screen.
 *
 * The records come from `listRecords` (`#/state/queries/capsule`,
 * `#/state/queries/flashbook`), which is what the classroom pickers read too — one
 * request per kind, answering with every field a card needs. Three kinds of record
 * are left out:
 *
 *  - **A draft (`status: 'draft') is the creator's own work in progress.** It has no
 *    post, so nobody else can open it, and it is not what a profile is for: a
 *    profile is what other people read. It is shown to the creator and to nobody
 *    else, badged as a draft, because the one thing a creator wants from their own
 *    page is to find the book they were in the middle of.
 *
 *  - **A record marked `visibility: 'classroom'` belongs to a room.** It *is* a
 *    post now — that is what lets the room it was written for read it and answer it
 *    — but the room is the only place it lives, which is why the app keeps it out of
 *    profiles, feeds and lists (see `#/state/queries/post-feed`). A profile tab that
 *    showed it would be undoing the one thing that mark is for.
 *
 *  - **A record with no post and no draft flag cannot be opened by anyone.** It is
 *    either a capsule made for a room before those were published as posts (the room
 *    that owns it draws it; see `ClassroomCapsuleCard`) or a book written by a
 *    build that never published it. There is nothing on a profile to play, so the
 *    grid would be a card that opens nothing.
 *
 * **One thing this is not: a guarantee.** `listRecords` is a public read, so a
 * creator's draft *records* are readable by anyone who asks for them — the appview's
 * `getActorFlashbooks` / `getActorCapsules` endpoints are the ones that decide which
 * drafts a viewer may see, and this module is a client-side filter over a public
 * list. Saying so matters: the day drafts have to be private, the answer is a server,
 * not this function.
 */
import {type ActorCapsuleRecord} from '#/state/queries/capsule'
import {type ActorFlashbookRecord} from '#/state/queries/flashbook'
import {type ActorModuleRecord} from '#/state/queries/module'
import {type WorkKind} from '#/components/work'

/** Where a piece of the creator's work stands. */
export type ProfileWorkStatus = 'published' | 'draft'

export type ProfileWorkItem = {
  kind: WorkKind
  /** The record's address: what opens the draft, and what keys the cell. */
  uri: string
  title: string
  description: string
  /** The post it was published as. Absent for a draft, by definition. */
  postUri?: string
  status: ProfileWorkStatus
  /** The grade code the publisher chose (`Level_A5`), not a name. */
  level?: string
  subject?: string
  /** Slides for a capsule, pages for a book — whichever the record counts in. */
  size?: number
  /** A book's cover still, when the record has one. */
  coverUri?: string
  createdAt: string
}

/** The capsules of one author, newest first, as a profile's tab shows them. */
export function profileCapsuleWork(
  records: ActorCapsuleRecord[] | undefined,
  {isMe}: {isMe: boolean},
): ProfileWorkItem[] {
  return order(
    (records ?? []).flatMap(record => {
      const status = record.status === 'draft' ? 'draft' : 'published'
      if (status === 'draft' && !isMe) return []
      if (record.visibility === 'classroom') return []
      if (status === 'published' && !record.postUri) return []
      return [
        {
          kind: 'capsule' as const,
          uri: record.uri,
          title: record.title.trim() || firstLine(record.description),
          description: record.description,
          postUri: record.postUri,
          status,
          level: record.level,
          subject: record.subject,
          size:
            record.slideCount && record.slideCount > 0
              ? record.slideCount
              : undefined,
          createdAt: record.createdAt,
        },
      ]
    }),
  )
}

/** The FlashBooks of one author, newest first, as a profile's tab shows them. */
export function profileFlashbookWork(
  records: ActorFlashbookRecord[] | undefined,
  {isMe}: {isMe: boolean},
): ProfileWorkItem[] {
  return order(
    (records ?? []).flatMap(record => {
      const status = record.status === 'draft' ? 'draft' : 'published'
      if (status === 'draft' && !isMe) return []
      if (record.visibility === 'classroom') return []
      if (status === 'published' && !record.postUri) return []
      return [
        {
          kind: 'flashbook' as const,
          uri: record.uri,
          title: record.title.trim() || firstLine(record.description),
          description: record.description,
          postUri: record.postUri,
          status,
          level: record.level,
          subject: record.subject,
          size:
            record.pageCount && record.pageCount > 0
              ? record.pageCount
              : undefined,
          /**
           * The record's own artwork, or the still derived from a film cover.
           *
           * Two sources because two generations of covers exist, in the order they
           * can be reached: a record's `imageUri` is the picture the reader plays,
           * and `videoThumbnailUri` is the still the video service derived for a
           * cover that is a film — which is also how the picker draws a book's card
           * (see `AddFlashbookDialog`).
           */
          coverUri: record.cover?.imageUri ?? record.cover?.videoThumbnailUri,
          createdAt: record.createdAt,
        },
      ]
    }),
  )
}

/**
 * The modules of one author, newest first, as a profile's Capsules tab shows them.
 *
 * The same three exclusions as the two above, and for the same reasons — which is
 * the whole argument for a module being a piece of work like any other rather than
 * a container with rules of its own:
 *
 *  - a **draft** (a module being assembled, which has no post and can be opened by
 *    nobody) is shown to its author alone, badged, and opens in the module builder;
 *  - a module marked `visibility: 'classroom'` belongs to the room it was built
 *    for, and a profile that listed it would undo the one thing that mark is for;
 *  - a module with no post and no draft flag is work nobody could open, so there is
 *    nothing on a profile to play.
 *
 * Its size is the number of *capsules* it holds, and that is the fact a card prints:
 * a module's own length is the length of its playlist, not pages or slides. It is
 * `itemCount` — the count the query read, so a playlist with an empty place left in
 * it still says how many places it has, which is the honest number while it is being
 * built and the same number once it is finished.
 */
export function profileModuleWork(
  records: ActorModuleRecord[] | undefined,
  {isMe}: {isMe: boolean},
): ProfileWorkItem[] {
  return order(
    (records ?? []).flatMap(record => {
      const status = record.status === 'draft' ? 'draft' : 'published'
      if (status === 'draft' && !isMe) return []
      if (record.visibility === 'classroom') return []
      if (status === 'published' && !record.postUri) return []
      return [
        {
          kind: 'module' as const,
          uri: record.uri,
          title: record.title.trim() || firstLine(record.description),
          description: record.description,
          postUri: record.postUri,
          status,
          level: record.level,
          subject: record.subject,
          /*
           * `0` is an answer here, not a missing one.
           *
           * A capsule or a book with no slides or pages is a record nobody has
           * begun, and the cell says nothing about it; a module with no capsules
           * is a *playlist with nothing in it*, which is the one thing its author
           * needs to see on the card — an empty playlist and a full one look
           * identical otherwise, both being a draft. So the count is passed
           * through as it is, zero included, and the cell decides how to say it.
           */
          size: record.itemCount,
          createdAt: record.createdAt,
        },
      ]
    }),
  )
}

/**
 * How many of each kind a profile's work holds, for the filter row.
 *
 * Counted from the *shown* items rather than from the records, so the numbers under
 * the choices are the numbers a reader will actually find by pressing them — a count
 * that included a visitor's view of somebody else's drafts would be a count of a
 * grid that never appears.
 */
export function countWorkKinds(
  items: readonly {kind: WorkKind}[],
): Partial<Record<WorkKind, number>> {
  const counts: Partial<Record<WorkKind, number>> = {}
  for (const item of items) {
    counts[item.kind] = (counts[item.kind] ?? 0) + 1
  }
  return counts
}

/**
 * Newest first, drafts and published work together.
 *
 * The question a profile's work tab answers is *what has this account made*, not
 * *what is out in the world* — so a draft written this morning stands where its work
 * began rather than in a section of its own, and the menu of a creator's own page
 * reads the way their desk looks.
 */
function order(items: ProfileWorkItem[]): ProfileWorkItem[] {
  return [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

/** The first line of a body of text, as a card's title. */
function firstLine(text: string): string {
  const [line] = text.trim().split('\n')
  return line?.trim() ?? ''
}
